import { useState, type Dispatch, type FormEvent, type SetStateAction } from "react";
import { Link } from "react-router";
import { HOLIDAY_REGIONS } from "@talentkontor/domain";
import { api } from "../../../data";
import { Dialog, PillView, useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { AdressVorschlag } from "../../../components/AdressVorschlag";
import type { Lang } from "../../creator/lang";
import { zonenAuswahl, zonenName } from "../zonen";
import {
  antworte, benachrichtige, loescheGruppe, loescheTermin, setzeUeberschreibungZurueck, speichereGruppe, speichereTermin, speichereTermineFuerCreator, speichereUeberschreibung,
  type KalenderDaten,
} from "./daten";
import {
  FARBEN, antwortPille, antwortText, artLabel, berichtText, empfaengerZahl, farbWert, formularAusTermin, hinzugefuegtVon, istAuto, leeresFormular, linkZiel,
  listenPille, pruefeGruppe, pruefeTermin, pruefeUeberschreibung, quelleText, tagLang, talentVoll, verantwortlich, zeitText, zielgruppeText,
  type Antwort, type Eintrag, type Farbe, type Nachschlag, type Perspektive, type TerminDaten, type TerminFormular, type Zielgruppe,
} from "./model";
import { Auswahl, Bestaetigen, Feld, FormFehler, Haken, Textfeld } from "./felder";
import { istCreatorKonto, t, ta } from "../../../lib/sprache";

/**
 * Dialoge des Kalenders: Termin-Übersicht, Termin anlegen/ändern, automatischen Termin anpassen, benachrichtigen, Gruppen.
 * 0056: Creator-Portal auf Englisch (Befund 9), Zusage/Absage/Rückfrage (Praxisfall), „Alle Creator“ und Gruppen nur für
 * wen es die Datenbank auch erlaubt (Befund 22), Standardzone neuer Termine der Agentur = Zone der Agentur (Befund 15).
 */

export type DialogZustand =
  | { art: "detail"; e: Eintrag }
  | { art: "termin"; e: Eintrag | null; datum: string }
  | { art: "anpassen"; e: Eintrag }
  | { art: "benachrichtigen"; e: Eintrag }
  | { art: "loeschen"; e: Eintrag }
  | { art: "gruppen" };

export interface KalenderKontext {
  perspektive: Perspektive;
  orgId: string;
  /** Zeitzone der betrachtenden Person */
  zone: string;
  /** Zeitzone der Agentur */
  orgZone: string;
  heute: string;
  n: Nachschlag;
  daten: KalenderDaten;
  neu: ReadonlySet<string>;
  eigeneTalentId: string | null;
  /** Sprache (Creator-Portal: eigene Wahl; Agentur: Deutsch) */
  lang: Lang;
  /** Sieht die Person alle Creator (Admin oder „alle Creator“)? Sonst kein „Alle Creator“ (Befund 22) */
  seesAll: boolean;
}

export function KalenderDialoge({ zustand, setZustand, k }: {
  zustand: DialogZustand | null;
  setZustand: Dispatch<SetStateAction<DialogZustand | null>>;
  k: KalenderKontext;
}) {
  const t = (de: string, en: string) => (k.lang === "en" ? en : de);
  // Schließt ein Dialog, weil ein anderer aufgeht (Übersicht → Löschen), meldet er sich per close-Ereignis:
  // dann nur den eigenen Zustand zurücksetzen, nicht den des neuen Dialogs.
  const zu = () => setZustand((z) => (z && z.art !== "loeschen" ? null : z));
  const zuLoeschen = () => setZustand((z) => (z?.art === "loeschen" ? null : z));
  const art = zustand?.art;
  const titel = (() => {
    if (!zustand) return "";
    if (zustand.art === "detail") return zustand.e.art === "holiday" ? t("Feiertag", "Public holiday") : istAuto(zustand.e) ? t("Automatischer Termin", "Automatic date") : t("Termin", "Date");
    if (zustand.art === "termin") return zustand.e ? t("Termin bearbeiten", "Edit date") : k.perspektive === "creator" ? t("Eigenen Termin anlegen", "Add your own date") : t("Termin anlegen", "Create appointment");
    if (zustand.art === "anpassen") return t("Automatischen Termin anpassen", "Adjust automatic appointment");
    if (zustand.art === "benachrichtigen") return t("Creator benachrichtigen", "Notify creators");
    if (zustand.art === "loeschen") return t("Termin löschen", "Delete date");
    return t("Gruppen für Termine", "Groups for appointments");
  })();
  return (
    <>
      <Dialog open={art === "detail" || art === "termin" || art === "anpassen" || art === "benachrichtigen" || art === "gruppen"} onClose={zu} title={titel}
        wide={art === "termin" || art === "gruppen"}>
        {zustand?.art === "detail" && <Detail key={zustand.e.key} e={zustand.e} k={k} setZustand={setZustand} />}
        {zustand?.art === "termin" && <TerminForm key={zustand.e?.key ?? `neu-${zustand.datum}`} e={zustand.e} datum={zustand.datum} k={k} onFertig={zu} />}
        {zustand?.art === "anpassen" && <Anpassen key={zustand.e.key} e={zustand.e} k={k} onFertig={zu} />}
        {zustand?.art === "benachrichtigen" && <Benachrichtigen key={zustand.e.key} e={zustand.e} k={k} onFertig={zu} />}
        {zustand?.art === "gruppen" && <Gruppen k={k} />}
      </Dialog>
      <Loeschen e={zustand?.art === "loeschen" ? zustand.e : null} onClose={zuLoeschen} lang={k.lang} />
    </>
  );
}

// ── Übersicht eines Eintrags (TA-123) ──────────────────────────────────────

function Detail({ e, k, setZustand }: { e: Eintrag; k: KalenderKontext; setZustand: Dispatch<SetStateAction<DialogZustand | null>> }) {
  const { busy, run } = useAction();
  const lang = k.lang;
  const en = lang === "en";
  const t = (de: string, en2: string) => (en ? en2 : de);
  const agentur = k.perspektive === "agentur";
  const zeit = zeitText(e, k.zone, lang);
  const link = linkZiel(e, k.perspektive, k.n, lang);
  const zeilen: [string, string][] = [
    [t("Art", "Type"), artLabel(e.art, lang)],
    [t("Datum", "Date"), e.ende ? `${tagLang(e.beginn, lang)} ${t("bis", "to")} ${tagLang(e.ende, lang)}` : tagLang(e.beginn, lang)],
    [t("Uhrzeit", "Time"), `${zeit.text}${zeit.hinweis ? `\n${zeit.hinweis}` : e.von && e.zeitzone ? ` (${e.zeitzone})` : ""}`],
    [t("Für wen", "For whom"), zielgruppeText(e, k.perspektive, k.n, lang)],
  ];
  const quelle = quelleText(e, k.n, lang);
  if (quelle) zeilen.push([t("Quelle", "Source"), quelle]);
  if (e.termin?.location) zeilen.push([t("Ort", "Location"), e.termin.location]);
  if (e.termin?.meeting_url) zeilen.push([t("Link", "Link"), e.termin.meeting_url]);
  if (e.termin?.description) zeilen.push([t("Beschreibung", "Description"), e.termin.description]);
  const von = hinzugefuegtVon(e, k.perspektive, k.n, lang);
  if (von) zeilen.push([t("Hinzugefügt von", "Added by"), von]);
  const wer = verantwortlich(e, k.perspektive, k.n, lang);
  if (wer) zeilen.push([t("Verantwortlich", "Responsible"), wer.charAt(0).toUpperCase() + wer.slice(1)]);
  if (e.mit?.length) zeilen.push([t("Zusammengefasst", "Combined"), t("Am selben Tag endet auch die Deadline der gesamten Kooperation.", "The deadline of the whole deal also ends on the same day.")]);
  if ((e.ueberschreibung || e.mit?.some((x) => x.ueberschreibung)) && !e.ausgeblendet) zeilen.push([t("Angepasst", "Adjusted"), ta("Titel, Datum oder Farbe hat das Team angepasst.", "The team has adjusted title, date or colour.", "Titel, Datum oder Farbe hast du angepasst.", "You adjusted title, date or colour.")]);

  // Region des Feiertags: eigene Wahl des Creators oder die der Agentur (Befund 13)
  const regionKey = k.daten.region;
  const region = HOLIDAY_REGIONS.find((r) => r.key === regionKey);
  const regionName = region ? (en ? region.label_en : region.label) : regionKey;
  const eigeneRegion = !agentur && !!k.daten.eigeneRegion;

  // Zusagen (0056): die Agentur sieht je Creator den Stand; der Creator sagt zu, ab oder fragt nach
  const antworten = e.termin ? k.daten.antworten.filter((a) => a.event_id === e.termin!.id) : [];
  const vonAgentur = e.art === "event" && e.termin && e.termin.created_by_side === "agency" && e.termin.audience !== "staff";

  // Aktionen gelten auch für mitgeführte Einträge am selben Tag
  const gruppe = [e, ...(e.mit ?? [])];
  const sichtbarkeit = (hidden: boolean) => async () => {
    for (const x of gruppe) {
      await speichereUeberschreibung(api, { orgId: k.orgId, eintrag: x, zone: k.orgZone, daten: { title: x.titel, starts_on: x.beginn, color: x.farbe, hidden } });
    }
    return true;
  };
  const einblenden = () => run("einblenden", sichtbarkeit(false), t("Termin wieder eingeblendet.", "Appointment shown again.")).then((r) => r && setZustand(null));
  const ausblenden = () => run("ausblenden", sichtbarkeit(true), t("Termin ausgeblendet. Unter „Ausgeblendete zeigen“ lässt er sich wieder einblenden.", "Appointment hidden. It can be shown again under “Show hidden”."))
    .then((r) => r && setZustand(null));
  const zuruecksetzen = () => run("zuruecksetzen", async () => {
    for (const x of gruppe) if (x.ueberschreibung) await setzeUeberschreibungZurueck(api, x.ueberschreibung.id);
    return true;
  }, t("Ursprüngliche Werte wiederhergestellt.", "Original values restored.")).then((r) => r && setZustand(null));

  return (
    <>
      <div className="row">
        <span className="tk-cal-punkt" style={{ background: farbWert(e.farbe) }} aria-hidden="true" />
        <strong>{e.titel}</strong>
        <PillView pill={listenPille(e, k.heute, k.neu.has(e.key), lang, k.n)} />
      </div>
      <div className="tk-cal-zeilen">
        {zeilen.map(([l, v]) => (
          <div key={l} className="tk-cal-zeile"><small>{l}</small>
            {/* K-527: Link klickbar, Ort mit Kartenlink */}
            {l === t("Link", "Link") && /^https:\/\//.test(v)
              ? <a href={v} target="_blank" rel="noopener noreferrer">{v.replace(/^https:\/\//, "")}</a>
              : l === t("Ort", "Location")
                ? <span>{v} · <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v)}`} target="_blank" rel="noopener noreferrer">{t("Karte", "Map")}</a></span>
                : <span>{v}</span>}
          </div>
        ))}
      </div>
      {e.art === "holiday" && (
        <p>{eigeneRegion
          ? t(`Gesetzlicher Feiertag deiner Region (${regionName}). Er ist schreibgeschützt.`, `Public holiday in your region (${regionName}). It is read-only.`)
          : t(`Gesetzlicher Feiertag der Region ${regionName ?? "der Agentur"}. Er ist schreibgeschützt.`, `Public holiday in ${regionName ?? "the agency's region"}. It is read-only.`)}</p>
      )}
      {!agentur && e.art === "event" && !e.bearbeitbar && <p>{t("Von deiner Agentur für dich angelegt. Nur zur Ansicht.", "Created for you by your agency. View only.")}</p>}
      {!agentur && istAuto(e) && <p>{t("Automatisch aus deinen Deals und Rechnungen. Anpassen kann ihn deine Agentur.", "Automatically from your deals and invoices. Your agency can adjust it.")}</p>}
      {!agentur && vonAgentur && k.eigeneTalentId && (
        <Zusage eventId={e.termin!.id} lang={lang} eigene={antworten.find((a) => a.talent_id === k.eigeneTalentId)?.response ?? null} />
      )}
      {agentur && vonAgentur && (
        <div className="stack">
          <small>{t("Rückmeldungen der Creator", "Creator responses")}</small>
          {antworten.length === 0 && <p className="muted">{t("Noch keine Zu- oder Absage.", "No acceptance or decline yet.")}</p>}
          {antworten.map((a) => (
            <div key={a.talent_id} className="row">
              <PillView pill={antwortPille(a.response)} />
              <span>{talentVoll(k.n.talent(a.talent_id))}{a.note ? `: „${a.note}“` : ""}</span>
            </div>
          ))}
        </div>
      )}
      <div className="row">
        {e.art === "event" && e.bearbeitbar && (
          <>
            <button type="button" className="primary" onClick={() => setZustand({ art: "termin", e, datum: e.beginn })}>{t("Bearbeiten", "Edit")}</button>
            <button type="button" onClick={() => setZustand({ art: "loeschen", e })}>{t("Löschen", "Delete")}</button>
          </>
        )}
        {agentur && e.art === "event" && e.bearbeitbar && e.termin && e.termin.audience !== "staff" && (
          <button type="button" onClick={() => setZustand({ art: "benachrichtigen", e })}>{t("Creator benachrichtigen", "Notify creators")}</button>
        )}
        {agentur && istAuto(e) && e.bearbeitbar && !e.ausgeblendet && (
          <>
            <button type="button" className="primary" onClick={() => setZustand({ art: "anpassen", e })}>{t("Anpassen", "Adjust")}</button>
            <button type="button" onClick={ausblenden} disabled={!!busy}>{busy === "ausblenden" ? t("Wird ausgeblendet …", "Hiding …") : t("Ausblenden", "Hide")}</button>
          </>
        )}
        {agentur && e.ausgeblendet && (
          <button type="button" className="primary" onClick={einblenden} disabled={!!busy}>{busy === "einblenden" ? t("Wird eingeblendet …", "Showing …") : t("Wieder einblenden", "Show again")}</button>
        )}
        {agentur && istAuto(e) && e.bearbeitbar && gruppe.some((x) => x.ueberschreibung) && (
          <button type="button" onClick={zuruecksetzen} disabled={!!busy}>{t("Ursprüngliche Werte", "Original values")}</button>
        )}
        {link && <Link className="btn" to={link.to}>{link.label}</Link>}
      </div>
    </>
  );
}

/** Zusage, Absage oder Rückfrage des Creators (Praxisfall). Die Rückfrage geht zusätzlich in den Chat. */
function Zusage({ eventId, eigene, lang }: { eventId: string; eigene: Antwort | null; lang: Lang }) {
  const en = lang === "en";
  const t = (de: string, en2: string) => (en ? en2 : de);
  const { busy, error, run } = useAction();
  const [frage, setFrage] = useState(false);
  const [notiz, setNotiz] = useState("");
  const senden = (a: Antwort) => void run(`antwort-${a}`, () => antworte(api, eventId, a, a === "accepted" ? null : notiz),
    a === "accepted" ? t("Zugesagt. Deine Agentur sieht es.", "Accepted. Your agency can see it.")
      : a === "declined" ? t("Abgesagt. Deine Agentur sieht es.", "Declined. Your agency can see it.")
        : t("Rückfrage gesendet. Die Antwort kommt im Chat.", "Question sent. The answer will come in the chat.")).then((r) => { if (r) { setFrage(false); setNotiz(""); } });
  return (
    <div className="stack tk-cal-zusage">
      <div className="row">
        <small>{t("Deine Antwort", "Your answer")}:</small>
        <PillView pill={antwortPille(eigene, lang)} />
      </div>
      {frage ? (
        <>
          <label>
            {t("Deine Rückfrage oder der Grund der Absage", "Your question or reason for declining")}
            <textarea rows={3} maxLength={500} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
          </label>
          <div className="row">
            <button type="button" className="primary" disabled={busy !== null || notiz.trim().length < 3} onClick={() => senden("question")}>{t("Rückfrage senden", "Send question")}</button>
            <button type="button" disabled={busy !== null} onClick={() => senden("declined")}>{t("Absagen", "Decline")}</button>
            <button type="button" onClick={() => setFrage(false)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </>
      ) : (
        <div className="row">
          <button type="button" className={eigene === "accepted" ? "" : "primary"} disabled={busy !== null || eigene === "accepted"} onClick={() => senden("accepted")}>{t("Zusagen", "Accept")}</button>
          <button type="button" disabled={busy !== null || eigene === "declined"} onClick={() => senden("declined")}>{t("Absagen", "Decline")}</button>
          <button type="button" disabled={busy !== null} onClick={() => setFrage(true)}>{t("Rückfrage", "Ask a question")}</button>
        </div>
      )}
      {eigene && <small>{t(`Aktuell: ${antwortText(eigene)}. Du kannst deine Antwort jederzeit ändern.`, `Current: ${antwortText(eigene, "en")}. You can change your answer at any time.`)}</small>}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </div>
  );
}

// ── Termin anlegen und ändern (AD-188, TA-122) ─────────────────────────────

function Farbwahl({ value, onChange, lang }: { value: Farbe; onChange: (f: Farbe) => void; lang: Lang }) {
  const FARB_EN: Record<Farbe, string> = { blue: "Blue", lightblue: "Light blue", green: "Green", yellow: "Yellow", orange: "Orange", red: "Red", pink: "Pink", purple: "Purple", grey: "Grey" };
  return (
    <fieldset className="tk-cal-feldgruppe">
      <legend>{lang === "en" ? "Colour" : "Farbe"}</legend>
      <div className="toolbar tk-cal-farben" role="group" aria-label={lang === "en" ? "Colour" : "Farbe"}>
        {FARBEN.map((f) => (
          <button key={f.key} type="button" aria-pressed={value === f.key} onClick={() => onChange(f.key)}>
            <i className="tk-cal-punkt" style={{ background: f.wert }} aria-hidden="true" />
            {lang === "en" ? FARB_EN[f.key] : f.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Zeitzone mit freier Wahl: übliche Zonen zuerst, dann alle, die der Browser kennt (Befund 15). */
function ZonenWahl({ value, onChange, fehler, eigene, lang, hinweis }: {
  value: string; onChange: (z: string) => void; fehler?: string | undefined; eigene: (string | null | undefined)[]; lang: Lang; hinweis: string;
}) {
  const z = zonenAuswahl(value, ...eigene);
  return (
    <label>
      {lang === "en" ? "Time zone" : "Zeitzone"}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={fehler ? true : undefined}>
        <optgroup label={lang === "en" ? "Common" : "Häufig"}>{z.haeufig.map((x) => <option key={x} value={x}>{zonenName(x)}</option>)}</optgroup>
        <optgroup label={lang === "en" ? "All time zones" : "Alle Zonen"}>{z.alle.map((x) => <option key={x} value={x}>{zonenName(x)}</option>)}</optgroup>
      </select>
      <small>{hinweis}</small>
      {fehler && <small className="tk-error">{fehler}</small>}
    </label>
  );
}

/** Hinweis beim Ort (Agentur-Sicht); im Studio (Creator-Konto) gibt es keine Mail an Creator (Studio-Texte F1) */
export function ortHinweis(lang: Lang): string {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  return istCreatorKonto() ? t("Steht im Termin, mit Kartenlink.", "Appears in the appointment, with a map link.")
    : t("Steht im Termin und in der Mail an die Creator. Im Termin gibt es dazu einen Kartenlink.", "Appears in the appointment and in the email to the creators. The appointment links to a map.");
}

function TerminForm({ e, datum, k, onFertig }: { e: Eintrag | null; datum: string; k: KalenderKontext; onFertig: () => void }) {
  const agentur = k.perspektive === "agentur";
  const lang = k.lang;
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  // Neue Termine der Agentur gelten in der Zone der Agentur (wer unterwegs einträgt, verschiebt sonst unbemerkt); eigene
  // Termine des Creators in seiner Zone (Befund 15)
  const [f, setF] = useState<TerminFormular>(() => (e?.termin ? formularAusTermin(e.termin, k.daten.termine) : leeresFormular(datum, agentur ? k.orgZone : k.zone, k.perspektive, k.eigeneTalentId)));
  // K-527: alle Zeilen eines Termins für mehrere Creator
  const geschwister = e?.termin?.copy_group ? k.daten.termine.filter((x) => x.copy_group === e.termin!.copy_group) : [];
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const { busy, error, run } = useAction();
  const set = <K extends keyof TerminFormular>(key: K) => (v: TerminFormular[K]) => setF((x) => ({ ...x, [key]: v }));
  const talente = k.daten.talents.filter((x) => x.status !== "archived" || x.id === f.talentId);
  // Zielgruppen nur, die die Datenbank für diese Person annimmt (Befund 22)
  const sichtbar = new Set(k.daten.talents.map((x) => x.id));
  const gruppenOk = k.daten.gruppen.filter((g) => k.daten.mitglieder.filter((m) => m.group_id === g.id).every((m) => sichtbar.has(m.talent_id)));
  const zielgruppen: { key: Zielgruppe; label: string }[] = [
    { key: "staff", label: t("Nur Team (intern)", "Team only (internal)") },
    ...(k.seesAll || f.zielgruppe === "all" ? [{ key: "all" as Zielgruppe, label: t("Alle Creator", "All creators") }] : []),
    ...(gruppenOk.length || f.zielgruppe === "group" ? [{ key: "group" as Zielgruppe, label: t("Eine Gruppe", "A group") }] : []),
    { key: "talent", label: t("Einzelne Creator", "Selected creators") },
  ];
  // Rückfrage vor einer Mail an viele Creator (Befund 6): „Alle“ oder Gruppe mit E-Mail
  const [frage, setFrage] = useState<{ daten: TerminDaten; talentIds: string[] } | null>(null);
  const massenmail = agentur && f.benachrichtigen && f.perMail && (f.zielgruppe === "all" || f.zielgruppe === "group");
  const anzahl = empfaengerZahl(f, k.daten.talents, k.daten.mitglieder);

  async function speichern(daten: TerminDaten, talentIds: string[]) {
    const res = await run("speichern", async () => {
      // K-527: Zielgruppe „Einzelne Creator“ der Agentur: je Creator eine Zeile mit gemeinsamer Gruppe
      if (agentur && daten.audience === "talent") {
        return speichereTermineFuerCreator(api, { orgId: k.orgId, termin: e?.termin ?? null, geschwister, daten, talentIds,
          benachrichtigen: f.benachrichtigen, perMail: f.perMail });
      }
      // andere Zielgruppe für einen bisherigen Termin mehrerer Creator: die übrigen Zeilen entfallen
      if (e?.termin?.copy_group) {
        for (const x of geschwister.filter((x) => x.id !== e.termin!.id)) await api.remove("calendar_events", [["id", "eq", x.id]]);
        await api.update("calendar_events", [["id", "eq", e.termin.id]], { copy_group: null });
      }
      return speichereTermin(api, { orgId: k.orgId, id: e?.termin?.id ?? null, daten, benachrichtigen: agentur && f.benachrichtigen, perMail: agentur && f.perMail });
    });
    setFrage(null);
    if (!res) return;
    const basis = e ? t("Termin gespeichert.", "Date saved.") : t("Termin angelegt.", "Date created.");
    if (res.benachrichtigt === null) toast(basis);
    else {
      const b = berichtText(res.benachrichtigt);
      toast(`${basis} ${b.text}${e ? t(" Gemeldet als „Termin geändert“ mit dem bisherigen Datum.", " Reported as “Appointment changed” with the previous date.") : ""}`);
      if (b.warnung) toast(b.warnung, "alert");
    }
    if (res.hinweis) toast(res.hinweis, "alert");
    onFertig();
  }
  async function absenden(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const r = pruefeTermin(f, k.perspektive, lang);
    setFehler(r.fehler);
    if (!r.daten) return;
    if ((massenmail || (agentur && f.benachrichtigen && f.perMail && r.talentIds.length > 1)) && anzahl > 1) {
      setFrage({ daten: r.daten, talentIds: r.talentIds });
      return;
    }
    await speichern(r.daten, r.talentIds);
  }

  return (
    <form className="stack" onSubmit={absenden} noValidate aria-busy={!!busy}>
      <Feld label={t("Titel", "Title")} value={f.titel} onChange={set("titel")} fehler={fehler.titel} required maxLength={200} autoFocus />
      <Textfeld label={t("Beschreibung", "Description")} value={f.beschreibung} onChange={set("beschreibung")} fehler={fehler.beschreibung} maxLength={2000} rows={3} />
      <div className="grid">
        <Feld label={t("Datum", "Date")} type="date" value={f.datum} onChange={set("datum")} fehler={fehler.datum} required />
        <Feld label={t("Bis (optional)", "Until (optional)")} type="date" value={f.bisDatum} onChange={set("bisDatum")} fehler={fehler.bisDatum} min={f.datum} hinweis={t("Für mehrtägige Termine", "For multi-day dates")} />
        <Feld label={t("Beginn (optional)", "Start (optional)")} type="time" value={f.von} onChange={set("von")} fehler={fehler.von} hinweis={t("Leer lassen für ganztägig", "Leave empty for all day")} />
        <Feld label={t("Ende (optional)", "End (optional)")} type="time" value={f.bis} onChange={set("bis")} fehler={fehler.bis} disabled={!f.von} />
      </div>
      {f.von && (
        <ZonenWahl value={f.zeitzone} onChange={set("zeitzone")} fehler={fehler.zeitzone} eigene={[k.zone, k.orgZone]} lang={lang}
          hinweis={agentur
            ? t(`Die Uhrzeit gilt in dieser Zone (Standard: Zone der Agentur, ${k.orgZone}). Für einen Dreh anderswo die Zone des Drehorts wählen; alle sehen die Uhrzeit in ihrer eigenen Zeit.`, `The time applies in this zone (default: the agency's zone, ${k.orgZone}). For a shoot elsewhere, choose the zone of the location; everyone sees the time in their own time zone.`)
            : t("Die Uhrzeit gilt in dieser Zone. Alle anderen sehen sie in ihrer eigenen Zeit.", "The time applies in this time zone. Everyone else sees it in their own time.")} />
      )}
      <Feld label={t("Ort (optional)", "Location (optional)")} value={f.ort} onChange={set("ort")} fehler={fehler.ort} maxLength={200}
        hinweis={agentur ? ortHinweis(lang) : undefined} />
      {/* K-527: Adressvorschläge beim Ort (wie bei Firmen- und Rechnungsadressen) */}
      <AdressVorschlag text={f.ort} lang={lang} fertig={(h) => h.label} onPick={(h) => set("ort")(h.label)} />
      <Feld label={t("Link für Calls (optional)", "Call link (optional)")} type="url" value={f.link} onChange={set("link")} fehler={fehler.link} maxLength={500} placeholder="https://"
        hinweis={t("Google Meet, Zoom oder Teams. Klickbar im Termin, in der Glocke, in der Mail und im Kalender-Abo.", "Google Meet, Zoom or Teams. Clickable in the appointment, the notifications, the email and the calendar subscription.")} />
      <Farbwahl value={f.farbe} onChange={set("farbe")} lang={lang} />
      {/* Studio (Creator-Konto): Termine sind für dich selbst; keine Zielgruppen, keine Creator-Auswahl, keine Benachrichtigung an Creator */}
      {agentur && !istCreatorKonto() && (
        <>
          <Auswahl label={t("Für wen", "For whom")} value={f.zielgruppe} onChange={(v) => set("zielgruppe")(v as Zielgruppe)} optionen={zielgruppen}
            hinweis={f.zielgruppe === "staff" ? t("Nur das Team sieht den Termin.", "Only the team sees the appointment.") : k.seesAll ? t("Creator sehen den Termin in ihrem Kalender.", "Creators see the appointment in their calendar.") : t("Creator sehen den Termin in ihrem Kalender. „Alle Creator“ können nur Personen wählen, die alle Creator sehen.", "Creators see the appointment in their calendar. “All creators” can only be chosen by people who see all creators.")} />
          {f.zielgruppe === "group" && (
            <Auswahl label={t("Gruppe", "Group")} value={f.gruppeId} onChange={set("gruppeId")} fehler={fehler.gruppeId} leer={t("Bitte wählen", "Please choose")}
              optionen={gruppenOk.map((g) => ({ key: g.id, label: g.name }))}
              hinweis={k.daten.gruppen.length ? (gruppenOk.length < k.daten.gruppen.length ? t("Gruppen mit Creatorn, die du nicht betreust, stehen hier nicht.", "Groups with creators you do not manage are not listed here.") : undefined)
                : t("Noch keine Gruppe angelegt. Gruppen legst du über „Gruppen“ im Kalender an.", "No group created yet. You create groups via “Groups” in the calendar.")} />
          )}
          {f.zielgruppe === "talent" && (
            <fieldset className="tk-cal-creatorwahl">
              <legend>{t("Creator", "Creators")} {f.talentIds.length > 0 && <small>· {t(`${f.talentIds.length} gewählt`, `${f.talentIds.length} selected`)}</small>}</legend>
              <div className="tk-cal-creatorliste">
                {talente.map((x) => (
                  <label key={x.id} className="row">
                    <input type="checkbox" checked={f.talentIds.includes(x.id)}
                      onChange={(ev) => setF((y) => ({ ...y, talentIds: ev.target.checked ? [...y.talentIds, x.id] : y.talentIds.filter((id) => id !== x.id), talentId: "" }))} />
                    <span>{talentVoll(x)}</span>
                  </label>
                ))}
              </div>
              {fehler.talentId && <p className="tk-error" role="alert">{fehler.talentId}</p>}
              <small className="muted">{t("Jeder gewählte Creator sieht den Termin in seinem Kalender, die anderen Namen nicht.", "Each selected creator sees the appointment in their calendar, but not the other names.")}</small>
            </fieldset>
          )}
          {f.zielgruppe !== "staff" && (
            <>
              <Haken label={t("Creator in der Glocke benachrichtigen", "Notify creators in the notifications")} checked={f.benachrichtigen} onChange={set("benachrichtigen")}
                hinweis={e ? t("Beim Bearbeiten nur ankreuzen, wenn sich für die Creator etwas Wichtiges ändert. Sie bekommen dann „Termin geändert“ mit dem bisherigen Datum; frühere Zusagen gelten nach einer Verschiebung nicht mehr.", "Only tick when editing if something important changes for the creators. They then get “Appointment changed” with the previous date; earlier acceptances no longer apply after a postponement.") : undefined} />
              <Haken label={t("Zusätzlich per E-Mail", "Also by email")} checked={f.benachrichtigen && f.perMail} onChange={set("perMail")} disabled={!f.benachrichtigen}
                hinweis={t("Die Mail geht an die hinterlegte Adresse des Creators; ohne Adresse oder bei abbestellten Hinweisen bleibt es bei der Glocke. Nach dem Speichern siehst du, wer wirklich eine Mail bekommt.", "The email goes to the creator's address on file; without an address or with notices unsubscribed, it stays in the notifications. After saving you will see who actually gets an email.")} />
            </>
          )}
        </>
      )}
      <FormFehler fehler={fehler} error={error} lang={lang} />
      {frage ? (
        <div className="notice stack" role="alert">
          <p>{t("Mail an ", "Send email to ")}{anzahl} {t("Creator senden? Alle bekommen den Termin zusätzlich in der Glocke.", "creators? Everyone also gets the appointment in the notifications.")}</p>
          <div className="row">
            <button type="button" className="primary" disabled={!!busy} onClick={() => void speichern(frage.daten, frage.talentIds)}>{busy ? t("Wird gespeichert …", "Saving …") : t(`Speichern und ${anzahl} Mails senden`, `Save and send ${anzahl} emails`)}</button>
            <button type="button" disabled={!!busy} onClick={() => setFrage(null)}>{t("Zurück", "Back")}</button>
          </div>
        </div>
      ) : (
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button>
          <button type="button" onClick={onFertig}>{t("Abbrechen", "Cancel")}</button>
        </div>
      )}
    </form>
  );
}

// ── Automatischen Termin anpassen (AD-190) ────────────────────────────────

function Anpassen({ e, k, onFertig }: { e: Eintrag; k: KalenderKontext; onFertig: () => void }) {
  const [f, setF] = useState({ titel: e.titel, datum: e.beginn, farbe: e.farbe });
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const { busy, error, run } = useAction();
  async function absenden(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const r = pruefeUeberschreibung(f);
    setFehler(r.fehler);
    if (!r.daten) return;
    const daten = r.daten;
    const ok = await run("anpassen", async () => {
      for (const x of [e, ...(e.mit ?? [])]) await speichereUeberschreibung(api, { orgId: k.orgId, eintrag: x, zone: k.orgZone, daten });
      return true;
    }, t("Termin angepasst.", "Appointment adjusted."));
    if (ok) onFertig();
  }
  return (
    <form className="stack" onSubmit={absenden} noValidate aria-busy={!!busy}>
      <p>{quelleText(e, k.n)}{t(". Die Anpassung gilt für alle, die den Termin sehen. Rechnung und Kooperation bleiben unverändert.", ". The adjustment applies to everyone who sees the appointment. Invoice and deal remain unchanged.")}</p>
      <Feld label={t("Titel", "Title")} value={f.titel} onChange={(v) => setF((x) => ({ ...x, titel: v }))} fehler={fehler.titel} maxLength={200} />
      <Feld label={t("Datum", "Date")} type="date" value={f.datum} onChange={(v) => setF((x) => ({ ...x, datum: v }))} fehler={fehler.datum}
        hinweis={t("Verschiebt nur den Eintrag im Kalender, nicht die Fälligkeit oder Frist. Eine Content-Deadline änderst du in der Kooperation; der Creator bekommt dann eine Nachricht.", "Only moves the calendar entry, not the due date or deadline. You change a content deadline in the deal; the creator then gets a message.")} />
      <Farbwahl value={f.farbe} onChange={(v) => setF((x) => ({ ...x, farbe: v }))} lang="de" />
      <FormFehler fehler={fehler} error={error} />
      <div className="row">
        <button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button>
        <button type="button" onClick={onFertig}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );
}

// ── Benachrichtigen (AD-189) ────────────────────────────────────────────────

function Benachrichtigen({ e, k, onFertig }: { e: Eintrag; k: KalenderKontext; onFertig: () => void }) {
  const [perMail, setPerMail] = useState(true);
  const [geaendert, setGeaendert] = useState(false);
  const [nurOffene, setNurOffene] = useState(false);
  const { busy, error, run } = useAction();
  const antworten = e.termin ? k.daten.antworten.filter((a) => a.event_id === e.termin!.id).length : 0;
  async function los() {
    const b = await run("benachrichtigen", () => benachrichtige(api, e.key, perMail, geaendert, nurOffene));
    if (b === undefined) return;
    const t = berichtText(b);
    toast(t.text);
    if (t.warnung) toast(t.warnung, "alert");
    onFertig();
  }
  return (
    <div className="stack">
      <p>„{e.titel}{t("“ am ", "” on ")}{tagLang(e.beginn)} · {zielgruppeText(e, k.perspektive, k.n)}{t(". Die Creator bekommen eine Meldung in der Glocke.", ". The creators get a notification.")}</p>
      <Haken label={t("Zusätzlich per E-Mail", "Also by email")} checked={perMail} onChange={setPerMail} hinweis={t("Ohne hinterlegte Adresse bleibt es bei der Glocke.", "Without an address on file, it stays in the notifications.")} />
      <Haken label={t("Als geänderten Termin melden", "Report as changed appointment")} checked={geaendert} onChange={setGeaendert}
        hinweis={t("Betreff „Termin geändert“ mit dem bisherigen Datum statt „Neuer Termin“; ältere Meldungen zum Termin verschwinden.", "Subject “Appointment changed” with the previous date instead of “New appointment”; older notifications about the appointment disappear.")} />
      <Haken label={t("Nur Creator ohne Zu- oder Absage erinnern", "Only remind creators without acceptance or decline")} checked={nurOffene} onChange={setNurOffene}
        hinweis={antworten ? t(`${antworten === 1 ? t("1 Creator hat", "1 creator has") : t(`${antworten} Creator haben`, `${antworten} creators have`)} schon geantwortet.`, `${antworten === 1 ? t("1 Creator hat", "1 creator has") : t(`${antworten} Creator haben`, `${antworten} creators have`)} already replied.`) : t("Bisher hat niemand geantwortet.", "Nobody has replied yet.")} />
      <FormFehler fehler={{}} error={error} />
      <div className="row">
        <button type="button" className="primary" onClick={los} disabled={!!busy}>{busy ? t("Wird gesendet …", "Sending …") : t("Benachrichtigen", "Notify")}</button>
        <button type="button" onClick={onFertig}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </div>
  );
}

function Loeschen({ e, onClose, lang }: { e: Eintrag | null; onClose: () => void; lang: Lang }) {
  const { busy, run } = useAction();
  const en = lang === "en";
  const mitCreatorn = !!e?.termin && e.termin.audience !== "staff" && e.termin.created_by_side === "agency";
  return (
    <Bestaetigen open={!!e} titel={en ? "Delete date" : "Termin löschen"} knopf={en ? "Delete" : "Löschen"} gefahr busy={!!busy} onClose={onClose} lang={lang}
      text={e ? (en ? `Delete “${e.titel}” on ${tagLang(e.beginn, "en")}? This cannot be undone.`
        : `„${e.titel}“ am ${tagLang(e.beginn)} löschen? Das lässt sich nicht rückgängig machen.${mitCreatorn ? " Creator, die über den Termin benachrichtigt wurden, bekommen eine Absage (Glocke und, falls sie eine Mail hatten, per Mail)." : ""}`) : ""}
      onJa={() => void run("loeschen", async () => {
        await loescheTermin(api, e!.key, e?.termin?.copy_group ?? null);
        return true;
      }, en ? "Date deleted." : mitCreatorn ? "Termin gelöscht. Benachrichtigte Creator bekommen eine Absage." : "Termin gelöscht.").then((r) => r && onClose())} />
  );
}

// ── Gruppen (Zielgruppe „Gruppe“) ──────────────────────────────────────────

function Gruppen({ k }: { k: KalenderKontext }) {
  const { busy, error, run } = useAction();
  const [bearbeite, setBearbeite] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null);
  const talente = k.daten.talents.filter((t) => t.status !== "archived");
  const sichtbar = new Set(k.daten.talents.map((t) => t.id));
  const mitglieder = (gid: string) => k.daten.mitglieder.filter((m) => m.group_id === gid).map((m) => m.talent_id);
  const neu = () => {
    setBearbeite(null);
    setName("");
    setAuswahl(new Set());
    setFehler({});
  };
  const oeffne = (gid: string) => {
    setBearbeite(gid);
    setName(k.daten.gruppen.find((g) => g.id === gid)?.name ?? "");
    setAuswahl(new Set(mitglieder(gid).filter((t) => sichtbar.has(t))));
    setFehler({});
  };
  async function speichern(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const r = pruefeGruppe(name, [...auswahl]);
    setFehler(r.fehler);
    if (Object.keys(r.fehler).length) return;
    const gid = await run("gruppe", () => speichereGruppe(api, {
      orgId: k.orgId, id: bearbeite, name: r.name, talentIds: [...auswahl], bisher: bearbeite ? mitglieder(bearbeite) : [], sichtbar,
    }), bearbeite ? t("Gruppe gespeichert.", "Group saved.") : t("Gruppe angelegt.", "Group created."));
    if (gid) neu();
  }
  const zuLoeschen = k.daten.gruppen.find((g) => g.id === loeschFrage);
  return (
    <div className="stack">
      <p>{t("Gruppen bündeln Creator, zum Beispiel für ein Event. Ein Termin für eine Gruppe erscheint bei allen Mitgliedern.", "Groups bundle creators, for example for an event. An appointment for a group appears for all members.")}</p>
      <section aria-label={t("Vorhandene Gruppen", "Existing groups")}>
        {k.daten.gruppen.map((g) => {
          const ids = mitglieder(g.id);
          const namen = ids.map((id) => k.n.talent(id)).filter((t) => !!t).map((t) => talentVoll(t));
          const weitere = ids.length - namen.length;
          // Ändern und Löschen nur, wer alle Mitglieder sieht (die Datenbank lehnt es sonst ab, Befund 22)
          return (
            <div key={g.id} className="task">
              <div>
                <strong>{g.name}</strong>
                <p>{namen.join(", ") || t("Keine sichtbaren Mitglieder", "No visible members")}{weitere > 0 ? t(` und ${weitere} weitere, die du nicht betreust`, ` and ${weitere} more you do not manage`) : ""}</p>
              </div>
              {weitere === 0 ? (
                <div className="row">
                  <button type="button" onClick={() => oeffne(g.id)} aria-label={t(`Gruppe ${g.name} bearbeiten`, `Edit group ${g.name}`)}>{t("Bearbeiten", "Edit")}</button>
                  <button type="button" onClick={() => setLoeschFrage(g.id)} aria-label={t(`Gruppe ${g.name} löschen`, `Delete group ${g.name}`)}>{t("Löschen", "delete")}</button>
                </div>
              ) : <small>{t("Ändern kann, wer alle Mitglieder sieht.", "Only someone who sees all members can change it.")}</small>}
            </div>
          );
        })}
        {k.daten.gruppen.length === 0 && <p>{t("Noch keine Gruppe angelegt.", "No group created yet.")}</p>}
      </section>
      <form className="stack panel" onSubmit={speichern} noValidate aria-busy={!!busy} aria-label={bearbeite ? t("Gruppe bearbeiten", "Edit group") : t("Neue Gruppe", "New group")}>
        <h3>{bearbeite ? t("Gruppe bearbeiten", "Edit group") : t("Neue Gruppe", "New group")}</h3>
        <Feld label="Name" value={name} onChange={setName} fehler={fehler.name} maxLength={80} />
        <fieldset className="tk-cal-feldgruppe">
          <legend>{t("Mitglieder", "Members")}</legend>
          <div className="tk-cal-auswahl">
            {talente.map((t) => (
              <label key={t.id} className="row">
                <input type="checkbox" checked={auswahl.has(t.id)} onChange={(ev) => setAuswahl((s) => {
                  const next = new Set(s);
                  if (ev.target.checked) next.add(t.id);
                  else next.delete(t.id);
                  return next;
                })} />
                {talentVoll(t)}
              </label>
            ))}
          </div>
          {fehler.mitglieder && <small className="tk-error">{fehler.mitglieder}</small>}
        </fieldset>
        <FormFehler fehler={{}} error={error} />
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy}>{busy === "gruppe" ? t("Wird gespeichert …", "Saving …") : bearbeite ? t("Gruppe speichern", "Save group") : t("Gruppe anlegen", "Create group")}</button>
          {bearbeite && <button type="button" onClick={neu}>{t("Neue Gruppe", "New group")}</button>}
        </div>
      </form>
      {zuLoeschen && (
        <div className="notice" role="alert">
          <p>{t("Gruppe „", "Delete group “")}{zuLoeschen.name}{t("“ löschen? Termine für diese Gruppe werden mitgelöscht.", "”? Appointments for this group will be deleted too.")}</p>
          <div className="row">
            <button type="button" className="danger" disabled={!!busy} onClick={() => void run("loeschen", async () => {
              await loescheGruppe(api, zuLoeschen.id);
              return true;
            }, t("Gruppe gelöscht.", "Group deleted.")).then((r) => {
              if (r) {
                setLoeschFrage(null);
                if (bearbeite === zuLoeschen.id) neu();
              }
            })}>{t("Gruppe löschen", "Delete group")}</button>
            <button type="button" onClick={() => setLoeschFrage(null)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
